import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import {
  Card,
  CardBody,
  CardHeader,
  CardRow,
  ErrorState,
  PageHeader,
  Skeleton,
  StatCard,
  YearPicker,
} from '../../components/ui';
import { cn } from '../../lib/cn';
import {
  formatDate,
  formatDateShort,
  formatMoney,
  formatMonth,
  formatPercent,
} from '../../lib/format';
import { useCashflow, type CashPeriodRow, type CashflowReport } from '../cashflow/api';
import { isPlanlessYear, PlanlessYearNotice } from '../cashflow/PlanlessYearNotice';
import { currentMonthKey, useDashboard } from './api';
import {
  EffectiveHourlyRate,
  NeedsAttention,
  ReceivablesAgeing,
  StaffUtilization,
} from './AttentionBlocks';
import { BonusCard } from './BonusCard';
import { CashBalanceChart, RevenueGoalChart } from './CashChart';
import { BreakdownBlock, InvoicesBlock } from './SideBlocks';

/**
 * `/dashboard` — **the money dashboard**.
 *
 * Jesse, 2026-08-12: *"Honestly, the budget and forecast dashboard is what I
 * want the finance dashboard to be… that's what I care most about. Replace the
 * dashboard with the b+f dashboard and leave the remaining tabs in the b+f
 * section."* So this screen IS the Budget & Forecast dashboard — moved up, not
 * copied — and `/budget-forecast` keeps Report and Projected only.
 *
 * Carried over from the old operational dashboard, on his instruction: **staff
 * utilization**, **needs attention** and **receivables ageing**. Dropped with
 * it: the expense-mix donut (the Breakdown block and the P&L cover it), the
 * revenue-vs-expenses and forecast-vs-actual charts, the trailing-12 cash card,
 * and the "cost cover" figure — this screen now prints `/api/cashflow`'s own
 * **runway**, so the app has ONE months-of-cash number rather than two words for
 * two denominators.
 *
 * ⚠ **ONE SOURCE, MANY VIEWS.** Every money figure here is a field of the same
 * `/api/cashflow` report the Report tab renders — the KPI strip, both charts,
 * the bonus card and the two blocks all read `query.data`. Nothing is
 * re-derived in the browser.
 *
 * ⚠ **TWO READINGS OF THE SAME INVOICES SIT ON THIS ONE SCREEN.** The Invoices
 * block rolls overdue money FORWARD into the current period (when will it
 * arrive); Receivables ageing ages the same invoices BACKWARDS by due date (how
 * late is it). Both are correct and both say which they are, in words. See
 * CLAUDE.md — this is exactly the pair the labelling rule exists for.
 *
 * ⚠ **TWO GOALS, TWO QUESTIONS.** The red line on the cash chart is a
 * CASH-BALANCE target; "Revenue vs goal" is the annual revenue goal
 * ($1,957,296). Neither is the other.
 */
export function DashboardPage() {
  const [params, setParams] = useSearchParams();
  const currentYear = new Date().getFullYear();
  const yearParam = Number(params.get('year'));
  const year = Number.isInteger(yearParam) && yearParam > 1970 ? yearParam : currentYear;

  const query = useCashflow(year);
  const report = query.data;

  /**
   * The operational modules answer "right now" questions — who is late, whose
   * support bank is empty, how the team is tracking THIS month — so they are
   * not year-scoped and there is no second picker to keep in step. One control
   * on the page, and it moves the money.
   */
  const month = currentMonthKey();
  const ops = useDashboard(month);

  const setYear = (next: number) => {
    const patched = new URLSearchParams(params);
    if (next === currentYear) patched.delete('year');
    else patched.set('year', String(next));
    setParams(patched, { replace: true });
  };

  /** The plan bar that owns the goal and the bonus thresholds. */
  const planHref = `/budget-forecast?tab=report${year === currentYear ? '' : `&year=${year}`}`;

  return (
    <div className={cn(query.isPlaceholderData && 'opacity-70 transition-opacity')}>
      <PageHeader
        title="Dashboard"
        /* One line, not three. At 390px the old subtitle plus the banner under
           it ate ~300px before a single figure, so the first screen of a money
           dashboard was prose. The link is the only part that does work. */
        /* No "Edit the plan" link (Jesse, 2026-08-20). The plan bar lives
           under the Report grid on Budget & Forecast, and the chart title
           below links there — a second door in the subtitle was chrome. */
        subtitle={<>Cash, runway and the {year} goal.</>}
        actions={
          <YearPicker
            year={year}
            years={report?.years ?? [currentYear - 1, currentYear, currentYear + 1]}
            onChange={setYear}
            ariaLabel="Dashboard year"
          />
        }
      />

      {query.isError ? (
        <ErrorState
          card
          title="Could not load the money dashboard"
          message={query.error instanceof Error ? query.error.message : undefined}
          onRetry={() => void query.refetch()}
        />
      ) : !report ? (
        <MoneySkeleton />
      ) : (
        /*
         * LAYOUT — **exact, as specified** (Jesse, 2026-08-12): *"staff
         * utilization should be on its own line at the bottom… receivables
         * aging, invoices, and breakdown should be on their own line in a 3
         * column spread."*
         *
         *  1. KPI strip
         *  2. Cash balance chart (⅔) │ **Needs attention** (⅓)
         *  3. Revenue vs goal (⅔)    │ Bonus status (⅓)
         *  4. Receivables ageing ⅓ │ Invoices ⅓ │ Breakdown ⅓   ← three equal columns
         *  5. Staff utilization ½ │ Effective hourly rate ½    ← the people row
         *
         * Row 4 supersedes the earlier "pair ageing with utilization" pass:
         * ageing/invoices/breakdown are three readings of the same receivable
         * money and belong on one line, and the two PEOPLE metrics — the least
         * urgent modules here — take the least prominent position, together.
         * Row 5 was utilization alone and full width until the effective
         * hourly rate joined it; see the row's own note for why it is two
         * cards rather than two figures in one.
         *
         * The three things this screen exists for — the KPI strip, the cash
         * chart and Needs attention — are rows 1 and 2, so none of them is
         * below the fold. Every row is the shared `CardRow`; no bespoke
         * widths, no second card style. Row 4 degrades 3 → 2 (from `md`) → 1
         * (below `md`), keeping that order; below `md` the whole screen is one
         * column.
         *
         * ⚠ **HEIGHTS — the two rules that pull against each other, and how
         * this screen settles them.** (a) *"can we have each row's card fit the
         * height given to it"* and *"can we have the graph fill in the height
         * that is given to it"*; (b) *"needs attention is probably going to be
         * the most variable module so we should be ready for it to push down"*
         * — it must not drag a chart to an absurd height. The BRIEF's own
         * resolution is a CEILING: fill the space, do not chase it, and beyond
         * the ceiling the extra height belongs to the tall neighbour.
         *
         * All of that now lives in the PRIMITIVE (`components/ui/Card.tsx`), so
         * this screen states layout and nothing else: `CardRow` is the
         * `items-stretch` grid, `Card fill="md|sm"` is the ceiling, and
         * `CardBody fill` hands the height down to a body that can spend it.
         * This file used to carry `xl:max-h-[560px]` on a bespoke wrapper div —
         * a per-screen answer to an app-wide question, which is how the app got
         * four month pickers.
         */
        <div className="space-y-4">
          {/*
            ⚠ **THE FIGURES BELOW HAVE A HISTORY, AND IT IS PRINTED.**
            A critic watched this dashboard's ending cash move 10.7×, its profit
            flip sign and its bonus projection fall $244,200 because a year's
            projected lines went away between two page loads — with nothing on
            screen to say the inputs had changed. The forecast is now
            soft-deleted, audited and restorable; this line is where the owner
            who looks on Monday and pays on Friday is TOLD the answer moved.
            It costs no click and it is present in the quiet case too.
          */}
          {/* The ForecastChangeBanner ("projected plan unchanged for N days")
              is GONE from this screen (Jesse, 2026-08-20: "remove the
              'projected plan history' row"). The audit trail it summarised is
              intact and one click away — ForecastHistory at the foot of the
              Projected tab's manual-lines card — and the soft-delete +
              restore machinery it fronted is untouched. Only the standing
              banner went. */}

          {/* One implementation, shared with the Report tab's grid — the two
              screens must not disagree about whether a year has been planned. */}
          <PlanlessYearNotice report={report} planHref={planHref} />

          <SummaryStrip report={report} />

          <CardRow cols={3}>
            <CashBalanceChart report={report} planHref={planHref} className="xl:col-span-2" />
            <Card>
              <CardHeader
                title="Needs attention"
                subtitle="Invoices late as of today · support banks running low"
                actions={<CardLink to="/invoices?state=LATE" label="Late invoices" />}
              />
              <CardBody>
                {ops.isError ? (
                  <ErrorState
                    message={
                      ops.error instanceof Error ? ops.error.message : 'Could not load this panel'
                    }
                    onRetry={() => void ops.refetch()}
                  />
                ) : !ops.data ? (
                  <div className="space-y-3" aria-busy>
                    {[0, 1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-10 w-full" />
                    ))}
                  </div>
                ) : (
                  <NeedsAttention data={ops.data} />
                )}
              </CardBody>
            </Card>
          </CardRow>

          <CardRow cols={3}>
            <RevenueGoalChart report={report} className="xl:col-span-2" />
            <BonusCard report={report} planHref={planHref} />
          </CardRow>

          {/*
            THE THREE-COLUMN ROW (Jesse, 2026-08-12: *"receivables aging,
            invoices, and breakdown should be on their own line in a 3 column
            spread"*). Three EQUAL columns — no `col-span` on any of them, so
            the row is a genuine thirds split rather than a wide card with two
            passengers.

            ⚠ **Why one class rides on the shared row here.** `cols={3}` is
            `xl:grid-cols-3` and *nothing between*, deliberately: every other
            three-column row in this app is a wide CHART plus a narrow card,
            and a chart squeezed to ~370px at tablet loses its axis labels
            before it loses white space (see `CardRow`'s own note). This row is
            three figure cards, which read perfectly well at half a tablet — so
            it takes the two-column step the existing variant does not carry.
            The row primitive still owns the mechanics (`items-stretch`, the
            gap, the height rule); a `3-equal` variant is proposed in the
            report so this class can go away.
          */}
          <CardRow cols={3} className="md:grid-cols-2">
            {/* ⚠ **"Billed, not yet revenue" IS THE SUBTITLE'S JOB NOW.**
                Revenue in this app is cash (see `pnl/revenue.ts`), so AR is no
                longer the same substance as the Revenue vs goal tile eight
                inches up — it is the queue in front of it. Saying so is what
                stops a reader adding the two, and it is the clause that makes
                AR gain meaning under the cash definition rather than lose it.
                The two-sided ageing rule (backwards here, forward on the cash
                forecast) still lives in `ReceivablesAgeing`'s own sentence and
                is untouched. */}
            <Card>
              <CardHeader
                title="Receivables ageing"
                subtitle={
                  ops.data
                    ? `Billed, not yet revenue · ${formatMoney(ops.data.ar.outstanding, { whole: true })} across ${ops.data.ar.invoiceCount} ${ops.data.ar.invoiceCount === 1 ? 'invoice' : 'invoices'} · as of today`
                    : 'Billed, not yet revenue · open invoices by days past due'
                }
                actions={<CardLink to="/invoices?focus=outstanding" label="Outstanding" />}
              />
              <CardBody>
                {ops.isError ? (
                  <ErrorState
                    message={
                      ops.error instanceof Error ? ops.error.message : 'Could not load this panel'
                    }
                    onRetry={() => void ops.refetch()}
                  />
                ) : !ops.data ? (
                  <div className="space-y-3" aria-busy>
                    <Skeleton className="h-3 w-full" />
                    <div className="grid grid-cols-2 gap-2">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} className="h-14 w-full" />
                      ))}
                    </div>
                  </div>
                ) : (
                  <ReceivablesAgeing data={ops.data} />
                )}
              </CardBody>
            </Card>

            <InvoicesBlock report={report} />
            {/* Three cards in a two-column tablet step leaves Breakdown alone
                on the second line; it spans that line rather than sitting in
                half of it beside a hole. Back to a third at `xl`. */}
            <BreakdownBlock report={report} className="md:col-span-2 xl:col-span-1" />
          </CardRow>

          {/*
            **THE PEOPLE ROW, LAST** — staff utilization and the effective
            hourly rate, side by side.
            (Jesse, 2026-08-12: *"staff utilization should be on its own line at
            the bottom"*, then *"I like effective hourly rate as a metric"* —
            *"it joins Staff utilization on the dashboard's bottom row"*.)

            **THE LAYOUT DECISION: two cards, not two figures in one card.**
            The alternative — utilization keeps the row and the rate becomes a
            second figure inside it — was rejected because these two metrics
            differ in FOUR ways at once, and every one of them needs saying:
            different population (staff only · everyone who tracked time),
            different unit (% · $/h), different window (month to date · year to
            date) and different denominators (capacity · every hour). Inside one
            card those four qualifiers would have to be crammed around two bare
            figures under a single header that could not honestly describe
            either. Two cards give each metric the one place a qualifier
            belongs — its own subtitle — and the BRIEF's warning is explicit
            that unlabelled side-by-side populations read as contradicting.

            **Why `2-xl` and not `2`.** Both bodies end in a meter with a
            labelled axis, and that is what sets the floor: at 1440 the content
            column is 1088px, so each card body is ~488px and both meters are
            comfortably legible. At the `md` step, though, the column is ~720px
            and a half is ~304px of body — below the ~250px the utilization
            axis was measured to collapse at, with no margin at all. So the pair
            splits only from `xl`; between `md` and `xl` each card takes the
            full width in turn, which is the same reading with more room rather
            than two cramped ones. Below `md` the whole screen is one column
            anyway.

            No `fill` on either card: their bodies are a figure, a meter and a
            sentence, so the taller of the two simply sets the row height and
            the shorter one ends where its content does — there is no chart here
            to stretch and no variable-length list to bound.
          */}
          <CardRow cols="2-xl">
            <Card>
              <CardHeader
                title="Staff utilization"
                /* ⚠ **THE DENOMINATOR IS IN THE SUBTITLE AND IT IS THE WHOLE
                   POINT.** This read "Billable ÷ tracked hours" while `/team`
                   printed billable ÷ capacity under the identical title — 63%
                   here, 49% one click away, same 8 staff, same month, both
                   captioned "target 75%". Utilization is ÷ CAPACITY
                   everywhere now; the other ratio is called billable share and
                   the body prints it by that name. */
                subtitle={`Billable ÷ capacity · staff only · ${formatMonth(`${month}-01`)} to date`}
                actions={<CardLink to="/team" label="Team" />}
              />
              <CardBody>
                {ops.isError ? (
                  <ErrorState
                    message={
                      ops.error instanceof Error ? ops.error.message : 'Could not load this panel'
                    }
                    onRetry={() => void ops.refetch()}
                  />
                ) : !ops.data ? (
                  <div className="space-y-3" aria-busy>
                    <Skeleton className="h-9 w-24" />
                    <Skeleton className="h-2 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                ) : (
                  <StaffUtilization
                    util={ops.data.utilization}
                    monthLabel={formatMonth(`${month}-01`)}
                    isCurrentMonth
                  />
                )}
              </CardBody>
            </Card>

            {/*
              ⚠ **THE SUBTITLE IS LOAD-BEARING, IN THREE WAYS.** "everyone who
              tracked time" against its neighbour's "staff only" is the whole
              defence against a reader taking two figures over two populations
              as one contradiction. "Jan–Aug 2026", named, is what stops the
              rate being read as a monthly figure — a month with no collection
              would print a near-zero rate and a month with two a fantasy.
              And **"Cash collected"** is the BASIS: revenue means money that
              arrived everywhere in this app, so the numerator of this rate is
              the same figure as the Revenue vs goal tile above. It used to read
              "Invoiced revenue", which was true of the old accrual definition
              and is now simply a second answer. Do not tidy any of the three
              away.
            */}
            <Card>
              <CardHeader
                title="Effective hourly rate"
                subtitle={
                  ops.data
                    ? `Cash collected ÷ every tracked hour · everyone who tracked time · ${ops.data.effectiveRate.windowLabel}`
                    : 'Cash collected ÷ every tracked hour · everyone who tracked time'
                }
                actions={<CardLink to="/reports/pnl" label="Profit & Loss" />}
              />
              <CardBody>
                {ops.isError ? (
                  <ErrorState
                    message={
                      ops.error instanceof Error ? ops.error.message : 'Could not load this panel'
                    }
                    onRetry={() => void ops.refetch()}
                  />
                ) : !ops.data ? (
                  <div className="space-y-3" aria-busy>
                    <Skeleton className="h-9 w-28" />
                    <Skeleton className="h-2 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                ) : (
                  <EffectiveHourlyRate metric={ops.data.effectiveRate} />
                )}
              </CardBody>
            </Card>
          </CardRow>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The KPI strip — the zero-click answers
// ---------------------------------------------------------------------------

/** KPI figures step down one size on phones so the 2-up card grid never
 *  truncates a dollar amount mid-number. */
function Figure({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn('block text-[1.125rem] leading-tight sm:text-heading-2', className)}>
      {children}
    </span>
  );
}


/**
 * How old the balance on screen is, in words, from the SERVER's `today` — the
 * browser's midnight is not the API's (`@db.Date` is UTC), and a card that
 * disagrees with the report it renders about what day it is would be a worse
 * bug than the one this fixes. Silent on the day itself; amber past a week,
 * because a fortnight-old bank balance is a fact about the process, not the
 * business.
 */
function ReadingAge({ asOf, today }: { asOf: string; today: string }) {
  const days = Math.round(
    (Date.parse(`${today}T00:00:00Z`) - Date.parse(`${asOf}T00:00:00Z`)) / 86_400_000,
  );
  if (!Number.isFinite(days) || days <= 0) return null;
  return (
    <span className={cn(days > 7 ? 'font-semibold text-status-partial' : 'text-grey-400')}>
      {' · '}
      {days === 1 ? '1 day old' : `${days} days old`}
    </span>
  );
}

/**
 * ⚠ **A RECORDED BALANCE BEATS A PROJECTION OF THE SAME FORTNIGHT.** It is the
 * rule the grid already lives by — an actual re-anchors the chain instead of
 * letting a projection compound on a projection — and the low-point line was
 * the one place that ignored it. Solid Digital's tile read "Low $195,736 · May
 * 16–31" against a RECORDED $233,950 for that period; Northline's read
 * "Goes negative — ($850) in May 16–31" in alarm red against a recorded
 * $34,300. A cash forecast raising a false insolvency alarm about a fortnight
 * three months in the past is the one thing this product exists not to do.
 */
const knownBalance = (p: CashPeriodRow): number => p.actualCash ?? p.endingCash;

function SummaryStrip({ report }: { report: CashflowReport }) {
  const currency = report.currency;
  const last = report.periods[report.periods.length - 1]!;
  const lowest = report.periods.reduce(
    (worst, p) => (knownBalance(p) < knownBalance(worst) ? p : worst),
    report.periods[0]!,
  );
  const lowestCash = knownBalance(lowest);
  /** An alarm is a call to act. A dip that has already happened — and that the
   *  bank statements since have contradicted — is history, so it keeps its date
   *  and loses the siren. */
  const lowestActionable = !lowest.isPast;
  const money = (value: number) => formatMoney(value, { currency, whole: true, parens: true });
  const runway = report.runway;
  /** The server owns the band; an API that predates it has no opinion, so fall
   *  back to the plain sign test rather than blanking the tile. */
  const netBand = runway.netBand ?? (runway.netPositive ? 'positive' : 'burning');
  /** No income at all in this year's report means the "runway" it implies is an
   *  artefact of an unplanned year, not a fact about the business. ONE
   *  definition, shared with the notice above and with the Report tab's grid —
   *  a strip that drops its alarm colours while the grid beside it keeps them
   *  is two answers to one question. */
  const planless = isPlanlessYear(report);
  /** Runway: the sooner of the projection's first below-$0 period and the
   *  trailing net burn (only while that burn is real), capped at the horizon. */
  const horizon = runway.runOutHorizonMonths ?? 12;
  const projected = runway.runOut ?? null;
  const trailing = netBand === 'burning' && runway.netMonths !== null ? runway.netMonths : null;
  const projectedLeads = projected !== null && (trailing === null || projected.months <= trailing);
  const runwayMonths = projectedLeads ? projected!.months : trailing;
  const runwayWithin = runwayMonths !== null && runwayMonths < horizon;
  const netMonthlyText = `${runway.netMonthly < 0 ? '−' : '+'}${formatMoney(Math.abs(runway.netMonthly), {
    currency,
    whole: true,
  })}/mo`;

  return (
    /*
     * ⚠ **FIVE TILES DO NOT TILE.** `cols={5}` is `grid-cols-2 xl:grid-cols-5`,
     * so between 640px and 1280px the strip ran 2-up in three rows with a hole
     * beside the fifth — at 1180 (a MacBook Air's default) it consumed the
     * entire viewport and put every chart below the fold. The fix is not a
     * sixth tile: it is a `lg` step of three, plus the ONE tile that can use
     * extra width (Revenue vs goal, which carries a bar and two captions)
     * spanning the leftover column at every step. No holes at 390, 768, 1180
     * or 1440, and the widest slot goes to the figure the screen is about.
     */
    <CardRow cols={5} className="gap-3 sm:gap-4 lg:grid-cols-3">
      {/*
        ⚠ **A BALANCE IS A READING, AND A READING HAS AN AGE.** "Snapshot Aug 7,
        2026" printed on August 12 said nothing about being five days old, and
        the tile beside it showed cash the business could not all spend: the
        same snapshot records $49,700 of liabilities, which the API returned and
        no screen had ever printed. Both are now on the card — the age in the
        hint, net cash in the footer — and neither costs a click.
      */}
      <StatCard
        kind="cash"
        label="Cash on hand"
        value={<Figure>{money(report.runway.currentCash)}</Figure>}
        hint={
          report.runway.asOf ? (
            <>
              {report.runway.source === 'period-actual'
                ? 'Reconciled'
                : report.runway.source === 'snapshot'
                  ? 'Snapshot'
                  : 'Projected'}{' '}
              {formatDate(report.runway.asOf)}
              <ReadingAge asOf={report.runway.asOf} today={report.today} />
            </>
          ) : (
            'No balance recorded'
          )
        }
        /* Two lines: at 390px "Snapshot Aug 7, 2026" clipped to "Snapshot Aug
           7, …" and the reader lost the YEAR of the balance they are reading. */
        hintLines={2}
        footer={
          report.runway.netCash !== null && report.runway.liabilities ? (
            <p className="text-caption leading-snug text-grey-400">
              less{' '}
              <span className="num">
                {formatMoney(report.runway.liabilities, { currency, whole: true })}
              </span>{' '}
              liabilities ={' '}
              <span className="num font-semibold text-grey-600">
                {money(report.runway.netCash)} net
              </span>
            </p>
          ) : undefined
        }
      />
      {/*
        ⚠ **RUNWAY IS THE SOONER OF TWO RUN-OUTS, AND IT NEVER CLAIMS MORE THAN
        12 MONTHS.** It used to be cash ÷ TRAILING net burn alone, printing ∞
        whenever the last three months were positive — and it said ∞ on
        July–September's +$32,724/mo while the grid it sits above went below
        $0 the next year (Jesse, 2026-10-06: *"does runway carry over years?
        … shouldn't be infinity"*). Now: the months until the PROJECTION first
        goes below $0 (`runway.runOut`, which walks across year boundaries) or
        until the trailing net burn empties the balance, whichever is sooner —
        and "12+ mo" when neither happens inside the next 12 months (*"you
        could also cap at 12 months and just say 12+ months"*). The trailing
        burn counts only while it is real: inside the ±10% break-even band it
        is noise and is ignored, as before. The zero-revenue stress test stays
        in the footer, named as what it is.
      */}
      <StatCard
        kind="period"
        label="Runway"
        value={
          <Figure>
            {planless ? '—' : runwayWithin ? `${runwayMonths!.toFixed(1)} mo` : `${horizon}+ mo`}
          </Figure>
        }
        valueClassName={
          planless
            ? undefined
            : runwayWithin
              ? runwayMonths! < 6
                ? 'text-primary-600'
                : undefined
              : 'text-success'
        }
        hint={
          planless
            ? `No ${report.year} revenue planned yet`
            : runwayWithin && projectedLeads ? (
              // Two lines clip in a five-tile strip: the date the plan runs out
              // is the fact; the trailing net is in the tooltip.
              <span title={`Trailing net ${netMonthlyText} · ${runway.windowLabel}`}>
                {`Plan below $0 by ${formatDate(projected!.end)}`}
              </span>
            ) : netBand === 'break-even' ? (
              // The band's own threshold lives in the tooltip.
              <span
                title={`The trailing net is inside ±${formatMoney(runway.netBandLimit ?? 0, {
                  currency,
                  whole: true,
                })}/mo — 10% of the ${formatMoney(runway.monthlyBurn, {
                  currency,
                  whole: true,
                })}/mo actually leaving — so it is treated as break-even, not as a duration.`}
              >
                {`Break-even · net ${netMonthlyText} · ${runway.windowLabel}`}
              </span>
            ) : runwayWithin ? (
              `Net ${netMonthlyText} · ${runway.windowLabel}`
            ) : (
              <span title={`Trailing net ${netMonthlyText} · ${runway.windowLabel}`}>
                {`Plan above $0 through ${formatMonth(runway.runOutThrough ?? report.today)}`}
              </span>
            )
        }
        hintLines={2}
        /* The gross-outflow figure is a STRESS TEST and now says so out loud,
           in the slot under the hint, instead of masquerading as the headline.
           ⚠ AND IT IS TWO FIGURES WHEN THE COST BASE HAS MOVED. Jesse cut team
           cost from $77,000/mo to $39,000/mo from August; the tile went on
           dividing by May–Jul's $78,333, which answers "how long would we last
           at the rate we spent before the cut?" — the one question a deliberate
           reduction stops you asking. The planned rate leads because it is what
           is being managed to; the trailing rate stays because it is the only
           one that has been proved. Each names its own months. */
        footer={
          planless || runway.months === null ? undefined : (
            <p className="text-caption text-grey-400">
              If revenue stopped:{' '}
              {runway.planDiffers && runway.plannedMonths !== null ? (
                <>
                  <span className="num font-semibold text-grey-600">
                    {runway.plannedMonths.toFixed(1)} mo
                  </span>{' '}
                  at the planned{' '}
                  {formatMoney(runway.plannedMonthlyOut ?? 0, { currency, whole: true })}/mo ·{' '}
                  {runway.plannedWindowLabel}
                  <br />
                  <span className="num font-semibold text-grey-500">
                    {runway.months.toFixed(1)} mo
                  </span>{' '}
                  at {formatMoney(runway.monthlyBurn, { currency, whole: true })}/mo actually
                  spent · {runway.windowLabel}
                </>
              ) : (
                <>
                  <span className="num font-semibold text-grey-600">
                    {runway.months.toFixed(1)} mo
                  </span>{' '}
                  at {formatMoney(runway.monthlyBurn, { currency, whole: true })}/mo out ·{' '}
                  {runway.windowLabel}
                </>
              )}
            </p>
          )
        }
      />
      <StatCard
        kind="profit"
        label={`Cash on ${formatDate(last.end)}`}
        /* A closed year ends on a RECORDED balance, not on the projection drawn
           before it closed: /dashboard?year=2025 printed the projected $257,925
           beside a chart whose own legend said "$244,340 recorded". */
        value={<Figure>{money(knownBalance(last))}</Figure>}
        /* A planless year's overdraft is an artefact of costs-with-no-revenue,
           not a forecast, so it does not get the alarm colour. */
        valueClassName={!planless && knownBalance(last) < 0 ? 'text-primary-600' : undefined}
        hint={
          planless ? (
            `Costs only — no ${report.year} revenue is planned yet`
          ) : lowest.key === last.key ? (
            last.actualCash !== null
              ? 'Recorded · the low point of the year'
              : 'The low point of the year'
          ) : lowestCash < 0 && lowestActionable ? (
            /*
             * ⚠ **GOING CASH-NEGATIVE IS THE ONE EVENT A CASH FORECAST EXISTS
             * TO WARN ABOUT**, and it was rendered in the same muted grey as
             * "Snapshot Aug 7, 2026". Northline's tile read "Dips to ($850) ·
             * May 16–31" in a subtitle colour. A projection that crosses zero
             * gets an alarm, a glyph and a link to the fortnight it happens in.
             */
            <Link
              to={`/budget-forecast?tab=report${report.year === new Date().getFullYear() ? '' : `&year=${report.year}`}`}
              className="inline-flex items-start gap-1 font-semibold text-primary-600 hover:underline"
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>
                Goes negative — {money(lowestCash)} in {lowest.label}
              </span>
            </Link>
          ) : (
            /* "…in May 16–31" clipped to "in May…" at 390px, which is a
               cash-low-point warning with no date on it. The third token says
               whether the figure is a bank balance or a projection — which is
               the whole point of preferring the recorded one. */
            `Low ${money(lowestCash)} · ${lowest.label} · ${
              lowest.actualCash !== null
                ? 'recorded'
                : 'projected'
            }`
          )
        }
        hintLines={2}
      />
      {/*
        THE PROJECTED STAT (Jesse, 2026-08-12): what the pipeline is worth to
        this year, with the uncounted half under it. Both come off
        `report.totals` — the SAME two figures the Projected tab foots to —
        because the checklist's ticks reach this report through the server.
      */}
      <StatCard
        kind="pipeline"
        label="Projected"
        value={<Figure>{money(report.totals.projectedAdditional)}</Figure>}
        hint={
          report.totals.projectedPotential > 0
            ? `counted · ${formatMoney(report.totals.projectedPotential, { currency, whole: true })} potential, not counted`
            : 'counted · nothing uncounted'
        }
        hintLines={2}
        to={`/budget-forecast?tab=projected${report.year === new Date().getFullYear() ? '' : `&year=${report.year}`}`}
      />
      <RevenueGoalStat report={report} planless={planless} />
    </CardRow>
  );
}

/**
 * **THE ONE NUMBER AN OWNER GLANCES AT EACH MORNING — and it used to be the
 * wrong one.**
 *
 * This tile headlined `totals.revenue` (a FULL-YEAR projection) over
 * `totals.goalPct` (that projection ÷ the FULL-YEAR goal) and printed
 * "$1,932,761 · 98.7% of $1,957,296" above a nearly-full bar — on a day the
 * agency was **$164,465 behind pace**, with $638,800 of that "revenue" being
 * projected lines and ticked deals nobody had invoiced. Three inches below it
 * the revenue chart's own subtitle read "$977,291 is booked". A dashboard that
 * contradicts its own chart is bad; a dashboard that contradicts it in the
 * reassuring direction is dangerous.
 *
 * So the headline is now **collected ÷ goal-to-date**, both sides covering the
 * same closed periods (`revenuePace`, computed once on the server), and the
 * full-year projection is demoted to the footer where it is labelled as a
 * projection and states how much of it is not yet invoiced.
 *
 * ⚠ **AND THE HEADLINE SAYS WHICH REVENUE IT IS.** It used to say "booked",
 * which in every other finance product means RECOGNISED/INVOICED — while this
 * figure is cash COLLECTED (Σ `periods[].collected` over the closed periods; a
 * period that has ended carries nothing else). The Effective hourly rate card
 * at the foot of this same screen divides by **invoiced** revenue, $1,102,494
 * YTD, so one screen carried two revenue-to-date figures $125K apart with only
 * one of them naming its basis. Both name it now; neither was wrong.
 *
 * **The bar is the argument.** Its full width is the annual goal; the solid
 * segment is banked money, the hatched one is the projection on top of it —
 * the same two marks the chart below uses — and the upright tick is where the
 * goal line stands TODAY. Behind pace is therefore a SHAPE (the solid fill
 * stops short of the tick), not a percentage a reader has to know to distrust.
 */
function RevenueGoalStat({ report, planless }: { report: CashflowReport; planless: boolean }) {
  /** Fills the leftover column at every step — see the strip's own note. */
  const span = 'col-span-2 lg:col-span-2 xl:col-span-1';
  const currency = report.currency;
  const pace = report.revenuePace;
  const money = (value: number) => formatMoney(value, { currency, whole: true });
  const hasGoal = report.plan.goal > 0;
  const behind = pace.gap < 0;
  const started = pace.periodsElapsed > 0;
  /** The last day both sides of the comparison cover — named, never implied. */
  const through = pace.through ? formatDateShort(pace.through) : null;

  if (!hasGoal) {
    return (
      <StatCard
        className={span}
        kind="revenue"
        label="Revenue collected"
        value={<Figure>{money(pace.booked)}</Figure>}
        hint={
          planless
            ? `Recurring schedules only · no ${report.year} goal set`
            : started
              ? `Cash banked through ${through} · no ${report.year} revenue goal set`
              : `Nothing has closed yet in ${report.year} · no revenue goal set`
        }
        hintLines={2}
        footer={
          <p className="text-caption leading-snug text-grey-400">
            Cash <span className="font-semibold text-grey-500">collected</span>, not invoiced. Full
            year agreements to{' '}
            <span className="num font-semibold text-grey-600">{money(pace.projected)}</span>
            {pace.unbooked > 0 && <> , incl. {money(pace.unbooked)} not yet invoiced</>}. Set a goal
            on Budget &amp; Forecast to see pace.
          </p>
        }
      />
    );
  }

  const label = `Cash collected ${money(pace.booked)} of the ${money(pace.goalToDate)} goal to date${
    through ? ` (through ${through})` : ''
  }; full year projected ${money(pace.projected)}, ${formatPercent(pace.projectedPct ?? 0)} of the ${money(report.plan.goal)} annual goal.`;

  return (
    <StatCard
      className={span}
      kind="revenue"
      label="Revenue vs goal"
      /* ⚠ COLLECTED, not projected. The figure and the percentage beside it
         are the same question over the same closed periods. */
      value={<Figure>{money(pace.booked)}</Figure>}
      hint={
        started ? (
          <>
            <span className={cn('font-semibold', behind ? 'text-primary-600' : 'text-success')}>
              {formatPercent(pace.pct ?? 0)} of goal to {through}
            </span>{' '}
            ·{' '}
            <span className={cn('num', behind ? 'text-primary-600' : 'text-success')}>
              {money(Math.abs(pace.gap))} {behind ? 'behind' : 'ahead'}
            </span>
          </>
        ) : (
          `No ${report.year} period has closed yet`
        )
      }
      hintLines={2}
      footer={
        <div className="space-y-1.5">
          <GoalPaceBar pace={pace} label={label} />
          {/* THE BASIS, THEN THE PROJECTION. The projection is still on the
              card — it is a real plan — but it is a caption under the bar, it
              carries the word "projected", and it names the part nobody has
              invoiced. The first clause is what stops the headline being read
              as invoiced revenue; see this component's own note. */}
          <p className="text-caption leading-snug text-grey-400">
            {/* ⚠ **THE WINDOW, ON THE BIG NUMBER.** This figure is cash in the
                CLOSED periods, because the goal it is measured against covers
                exactly those periods — while the Breakdown card lower down
                prints all of the year's cash ($1,004,839 on seed). Two honest
                "collected" figures $27,548 apart sat on one screen naming
                neither window; each names its own now. */}
            Cash <span className="font-semibold text-grey-500">collected</span>
            {through && (
              <>
                {' '}
                <span className="font-semibold text-grey-500">through {through}</span>
              </>
            )}
            , not invoiced{through && ' — same periods as the goal'} · Projected{' '}
            <span className="num font-semibold text-grey-600">{money(pace.projected)}</span> (
            {formatPercent(pace.projectedPct ?? 0)})
            {pace.unbooked > 0 && (
              <>
                , incl. <span className="num">{money(pace.unbooked)}</span> not yet invoiced
              </>
            )}
          </p>
        </div>
      }
    />
  );
}

/** Full width = the annual goal. See `RevenueGoalStat` for why the marker is
 *  the point of it. Colours match the chart below: solid = collected, hatched
 *  = forecast, and the tick is the same navy as the cash line. */
function GoalPaceBar({
  pace,
  label,
}: {
  pace: CashflowReport['revenuePace'];
  label: string;
}) {
  const pct = (value: number | null) => Math.min(100, Math.max(0, (value ?? 0) * 100));
  const booked = pct(pace.bookedPct);
  const projected = pct(pace.projectedPct);
  const marker = pct(pace.elapsedPct);
  const met = (pace.pct ?? 0) >= 1;
  return (
    /* `overflow-visible` on the frame and `overflow-hidden` on the TRACK: the
       marker overhangs the bar by 2px top and bottom so it reads as a
       reference line rather than as one more segment. */
    <div className="relative h-2 w-full" role="img" aria-label={label} title={label}>
      <div className="absolute inset-0 overflow-hidden rounded-full bg-grey-100">
        <div
          className="absolute inset-y-0 left-0"
          style={{
            width: `${projected}%`,
            backgroundImage:
              'repeating-linear-gradient(45deg, #93c5fd 0 2px, #eff6ff 2px 5px)',
          }}
        />
        <div
          className={cn('absolute inset-y-0 left-0', met ? 'bg-status-paid' : 'bg-accent')}
          style={{ width: `${booked}%` }}
        />
      </div>
      <span
        className="absolute -top-[3px] -bottom-[3px] w-[2px] rounded-full bg-secondary"
        style={{ left: `calc(${marker}% - 1px)` }}
        aria-hidden
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chrome
// ---------------------------------------------------------------------------

function CardLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 text-body-sm font-semibold text-accent hover:text-accent-700"
    >
      {label}
    </Link>
  );
}

/** The money half waits in the shape it will land in. */
function MoneySkeleton() {
  return (
    <div className="space-y-4">
      {/* Same steps and the same spanning fifth tile the real strip lands in —
          a skeleton in the wrong shape is a layout jump. */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-5">
        {[0, 1, 2, 3, 4].map((i) => (
          <Card
            key={i}
            padding="md"
            className={i === 4 ? 'col-span-2 lg:col-span-2 xl:col-span-1' : undefined}
          >
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-3 h-8 w-32" />
            <Skeleton className="mt-2 h-3 w-40" />
          </Card>
        ))}
      </div>
      {/* Same two rows the real thing lands in: a wide chart with a card
          beside it, twice. A skeleton in the wrong shape is a layout jump —
          including its alignment, so these are the same `CardRow` the loaded
          screen uses rather than a hand-written `items-start` grid. */}
      {[0, 1].map((row) => (
        <CardRow key={row} cols={3}>
          <Card padding="md" className="xl:col-span-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="mt-3 h-[240px] w-full" />
          </Card>
          <Card padding="md" fill="natural">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="mt-3 h-4 w-full" />
            <Skeleton className="mt-2 h-4 w-full" />
            <Skeleton className="mt-2 h-4 w-2/3" />
          </Card>
        </CardRow>
      ))}
      {/* Row 4 lands as three equal columns and row 5 as a pair, so the
          skeleton waits in exactly those shapes — a skeleton in the wrong
          shape is a layout jump. */}
      <CardRow cols={3} className="md:grid-cols-2">
        {[0, 1, 2].map((i) => (
          <Card key={i} padding="md" fill="natural">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="mt-3 h-3 w-full" />
            <Skeleton className="mt-3 h-14 w-full" />
          </Card>
        ))}
      </CardRow>
      <CardRow cols="2-xl">
        {[0, 1].map((i) => (
          <Card key={i} padding="md">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="mt-3 h-8 w-24" />
            <Skeleton className="mt-3 h-2 w-full" />
          </Card>
        ))}
      </CardRow>
    </div>
  );
}
